import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Unsubscribe.scss";
import { useAuthContext } from "../../../hooks/context/AuthContext";

const Unsubscribe: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const { webUnsub } = useAuthContext();

  // With a properly URL-encoded link, this returns the literal email address.
  const email = useMemo(() => (searchParams.get("email") ?? "").trim(), [searchParams]);

  const [options, setOptions] = useState<{ promotions: boolean; productUpdates: boolean }>({
    promotions: false,
    productUpdates: false,
  });

  const subs = useMemo(() => {
    const s: string[] = [];
    if (options.promotions) s.push("promo");
    if (options.productUpdates) s.push("product-updates");
    return s;
  }, [options.promotions, options.productUpdates]);

  const canSubmit = useMemo(() => !!email && subs.length > 0 && !isSubmitting, [email, subs.length, isSubmitting]);

  const onCancel = useCallback(() => {
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  const onOptionChange = useCallback((key: "promotions" | "productUpdates") => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const onUnsubscribe = useCallback(() => {
    if (!email || subs.length === 0) return;

    setIsSubmitting(true);

    webUnsub(
      email,
      subs,
      () => {
        setIsSubmitting(false);
        setShowDialog(true);
      },
      (err) => {
        console.error("Unsubscribe failed:", err);
        setIsSubmitting(false);
      }
    );
  }, [email, subs, webUnsub]);

  const onDialogKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === "Escape" || e.key === " ") {
        onCancel();
      }
    },
    [onCancel]
  );

  return (
    <div className="Unsubscribe">
      <div className="unsub-card" role="region" aria-labelledby="unsub-heading">
        <h1 id="unsub-heading" className="sr-only">
          Unsubscribe
        </h1>

        <p className="unsub-text">Select the emails you want to unsubscribe from:</p>

        <fieldset className="unsub-options" aria-describedby="unsub-options-help">
          <legend className="sr-only">Unsubscribe options</legend>

          <label className="unsub-option">
            <input
              type="checkbox"
              checked={options.promotions}
              onChange={() => onOptionChange("promotions")}
              disabled={isSubmitting}
            />
            <span className="unsub-option-label">Promotional Offers</span>
          </label>

          <label className="unsub-option">
            <input
              type="checkbox"
              checked={options.productUpdates}
              onChange={() => onOptionChange("productUpdates")}
              disabled={isSubmitting}
            />
            <span className="unsub-option-label">Important Product Updates</span>
          </label>

          <p id="unsub-options-help" className="unsub-options-help" aria-live="polite">
            Select at least one option to continue.
          </p>
        </fieldset>

        <div className="unsub-actions">
          <button type="button" className="btn btn-primary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={onUnsubscribe}
            disabled={!canSubmit}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? "Unsubscribing…" : "Unsubscribe"}
          </button>
        </div>
      </div>

      {showDialog && (
        <div className="unsub-modal-backdrop" role="presentation">
          <div
            className="unsub-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="unsub-success-title"
            tabIndex={-1}
            onKeyDown={onDialogKeyDown}
          >
            <h2 id="unsub-success-title">You have unsubscribed.</h2>
            <div className="unsub-modal-actions">
              <button type="button" className="btn btn-primary" onClick={onCancel} autoFocus>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Unsubscribe;
